import prepareComponent from "@bluframe/grapple/prepareComponent"
import {
  SocialsHandle,
  SocialsHandlesWaveFilters
} from "@bluframe/mightychroma-mightytool-frontend-types"
import { ChangeEventHandler, useEffect, useMemo, useRef } from "react"

import useDraftOverrides from "./hooks/useDraftOverrides"
import ProspectQueue from "./ProspectQueue"
import type { OverrideUpdatePayload } from "./types"

export type FiltersState = SocialsHandlesWaveFilters

export interface WavePaginationState {
  link: string
  cursor: string | null
  hasNextData: boolean
  hasPrevData: boolean | null
  prevCursor: string | null
  page?: number
  totalPages?: number
}

export type { OverrideUpdatePayload } from "./types"

export interface Props {
  errorMessage?: string | null
  filters: FiltersState
  handles: SocialsHandle[]
  isLoading: boolean
  pagination: WavePaginationState
  selectedHandle?: SocialsHandle | null
  onCloseDetail: () => void
  onFiltersChange: (filters: FiltersState) => void
  onOverrideUpdate: (payload: OverrideUpdatePayload) => void
  onRetry?: () => void
  onSelectHandle: (handle: SocialsHandle) => void
}

export interface ComponentProps extends Props {
  draftManualPriority: string
  draftNotes: string
  draftIsIgnored: boolean
  hasActiveFilters: boolean
  isOverrideSaveDisabled: boolean
  panelRef: React.RefObject<HTMLDivElement | null>
  onClearFilters: () => void
  onDraftIsIgnoredChange: (value: string | number) => void
  onDraftManualPriorityChange: (value: string) => void
  onDraftNotesChange: ChangeEventHandler<HTMLTextAreaElement>
  onJustInteracted: () => void
  onSaveOverrides: () => void
  onSelectHandleClick: (
    handle: SocialsHandle
  ) => (event: React.MouseEvent<HTMLButtonElement>) => void
}

const hasNonEmptyValue = (value: unknown): boolean => {
  if (value === undefined || value === null) return false
  if (Array.isArray(value)) return value.length > 0
  return true
}

// eslint-disable-next-line max-statements
const usePrepareComponent = (props: Props): ComponentProps => {
  const lastTriggerRef = useRef<HTMLElement | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const previousSelectedHandleIdRef = useRef<string | null>(null)

  const selectedHandle = props.selectedHandle ?? null
  const selectedHandleId = selectedHandle?.id ?? null

  const hasActiveFilters = useMemo(
    () => Object.values(props.filters).some((value) => hasNonEmptyValue(value)),
    [props.filters]
  )

  const draftOverrides = useDraftOverrides(
    selectedHandle,
    props.onOverrideUpdate
  )

  useEffect(() => {
    if (
      selectedHandleId &&
      selectedHandleId !== previousSelectedHandleIdRef.current
    ) {
      requestAnimationFrame(() => panelRef.current?.focus())
    }

    if (!selectedHandleId && previousSelectedHandleIdRef.current) {
      requestAnimationFrame(() => lastTriggerRef.current?.focus())
    }

    previousSelectedHandleIdRef.current = selectedHandleId
  }, [selectedHandleId])

  const onSelectHandleClick =
    (handle: SocialsHandle) => (event: React.MouseEvent<HTMLButtonElement>) => {
      lastTriggerRef.current = event.currentTarget
      props.onSelectHandle(handle)
    }

  const onClearFilters = () => props.onFiltersChange({})

  return {
    ...props,
    ...draftOverrides,
    hasActiveFilters,
    onClearFilters,
    onSelectHandleClick,
    panelRef,
    selectedHandle
  }
}

export default prepareComponent<Props, ComponentProps>(usePrepareComponent)(
  ProspectQueue
)
